import { useEffect, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { fmtUSD, fmtNum, fmtPct, fmtDate, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import Documents from "@/pages/portal/Documents";
import Deposit from "@/pages/portal/Deposit";

function Tile({ label, value, sub, testid }) {
  return (
    <div className="stat-tile" data-testid={testid}>
      <div className="kicker mb-2">{label}</div>
      <div className="font-mono text-2xl">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function Dashboard() {
  const [holdings, setHoldings] = useState([]);
  useEffect(() => { api.get("/client/holdings").then((r) => setHoldings(r.data)); }, []);
  const total = holdings.reduce((s, h) => s + h.holding_value, 0);
  const totalGain = holdings.reduce((s, h) => s + h.unrealized_gain, 0);
  return (
    <div>
      <div className="mb-8">
        <div className="kicker mb-2">Consolidated</div>
        <h1 className="text-4xl">Your holdings across Piplight funds</h1>
      </div>
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Tile label="Total Value" value={fmtUSD(total)} testid="dash-total-value" />
        <Tile label="Unrealized P/L" value={<span className={totalGain >= 0 ? "pos" : "neg"}>{fmtUSD(totalGain)}</span>} testid="dash-total-pl" />
        <Tile label="Funds Held" value={holdings.length} testid="dash-fund-count" />
        <Tile label="As Of" value={<span className="text-sm">{new Date().toLocaleDateString()}</span>} testid="dash-asof" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {holdings.map((h) => (
          <div key={h.id} data-testid={`holding-card-${h.fund_id}`} className="surface p-5">
            <div className="kicker mb-2">{h.fund?.code}</div>
            <h3 className="text-lg mb-4">{h.fund?.name}</h3>
            <dl className="text-sm space-y-2.5">
              <Row k="Units held" v={fmtNum(h.unit_balance, 4)} />
              <Row k="NAV / Unit" v={fmtNum(h.nav_per_unit, 4)} />
              <Row k="Holding value" v={fmtUSD(h.holding_value)} />
              <Row k="Unrealized" v={<span className={h.unrealized_gain >= 0 ? "pos" : "neg"}>{fmtUSD(h.unrealized_gain)} · {h.unrealized_gain_pct >= 0 ? "+" : ""}{fmtPct(h.unrealized_gain_pct)}</span>} />
              <Row k="HWM (NAV)" v={fmtNum(h.high_water_mark_nav, 4)} />
              <Row k="Subscribed" v={fmtDate(h.subscription_start_date)} />
              <Row k="Next Withdrawal" v={<span className="gold">{fmtDate(h.next_withdrawal_date)}</span>} />
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}
const Row = ({ k, v }) => (
  <div className="flex justify-between border-b border-border/60 pb-1.5">
    <dt className="text-muted-foreground">{k}</dt><dd className="font-mono">{v}</dd>
  </div>
);

function Holdings() {
  const [holdings, setHoldings] = useState([]);
  useEffect(() => { api.get("/client/holdings").then((r) => setHoldings(r.data)); }, []);
  return (
    <div>
      <h1 className="text-3xl mb-6">Holdings by Fund</h1>
      <table className="w-full text-sm">
        <thead className="text-muted-foreground text-xs uppercase tracking-widest">
          <tr><th className="text-left pb-3">Fund</th><th className="text-right pb-3">Units</th><th className="text-right pb-3">NAV/Unit</th><th className="text-right pb-3">Value</th><th className="text-right pb-3">Unrealized</th><th className="text-right pb-3">HWM</th><th className="text-right pb-3">Next Withdrawal</th></tr>
        </thead>
        <tbody>
          {holdings.map((h) => (
            <tr key={h.id} className="border-t border-border row-hover" data-testid={`hold-row-${h.fund_id}`}>
              <td className="py-3">{h.fund?.name}</td>
              <td className="py-3 text-right font-mono">{fmtNum(h.unit_balance, 4)}</td>
              <td className="py-3 text-right font-mono">{fmtNum(h.nav_per_unit, 4)}</td>
              <td className="py-3 text-right font-mono">{fmtUSD(h.holding_value)}</td>
              <td className={`py-3 text-right font-mono ${h.unrealized_gain >= 0 ? "pos" : "neg"}`}>{fmtUSD(h.unrealized_gain)}</td>
              <td className="py-3 text-right font-mono">{fmtNum(h.high_water_mark_nav, 4)}</td>
              <td className="py-3 text-right font-mono gold">{fmtDate(h.next_withdrawal_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Transactions() {
  const [txs, setTxs] = useState([]);
  useEffect(() => { api.get("/client/transactions").then((r) => setTxs(r.data)); }, []);
  return (
    <div>
      <h1 className="text-3xl mb-6">Unit Ledger</h1>
      <table className="w-full text-sm">
        <thead className="text-muted-foreground text-xs uppercase tracking-widest">
          <tr><th className="text-left pb-3">Timestamp</th><th className="text-left pb-3">Type</th><th className="text-left pb-3">Fund</th><th className="text-right pb-3">Units Δ</th><th className="text-right pb-3">NAV Applied</th><th className="text-right pb-3">Amount</th></tr>
        </thead>
        <tbody>
          {txs.map((t) => (
            <tr key={t.id} className="border-t border-border row-hover" data-testid={`tx-row-${t.id}`}>
              <td className="py-3 font-mono">{fmtDateTime(t.timestamp)}</td>
              <td className="py-3 uppercase text-xs tracking-widest">{t.type}</td>
              <td className="py-3 font-mono">{t.fund_id}</td>
              <td className={`py-3 text-right font-mono ${t.units_delta >= 0 ? "pos" : "neg"}`}>{fmtNum(t.units_delta, 4)}</td>
              <td className="py-3 text-right font-mono">{fmtNum(t.nav_per_unit_applied, 4)}</td>
              <td className="py-3 text-right font-mono">{fmtUSD(t.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Requests() {
  const [funds, setFunds] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [f, setF] = useState({ kind: "subscription", fund_id: "", amount: 25000, units: "" });
  const load = () => api.get("/client/requests").then((r) => setReqs(r.data));
  useEffect(() => { api.get("/funds").then((r) => { setFunds(r.data); setF((x) => ({ ...x, fund_id: r.data[0]?.id || "" })); }); load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (f.kind === "subscription") await api.post("/client/subscribe", { fund_id: f.fund_id, amount: Number(f.amount) });
      else await api.post("/client/redeem", { fund_id: f.fund_id, amount: f.amount ? Number(f.amount) : null, units: f.units ? Number(f.units) : null });
      toast.success("Request submitted for review.");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div>
        <h1 className="text-3xl mb-6">New Request</h1>
        <form onSubmit={submit} className="surface p-6 space-y-4">
          <div className="flex gap-2">
            {["subscription", "redemption"].map((k) => (
              <button type="button" key={k} onClick={() => setF({ ...f, kind: k })}
                data-testid={`req-kind-${k}`}
                className={`px-4 py-2 text-xs uppercase tracking-widest border ${f.kind === k ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>{k}</button>
            ))}
          </div>
          <label className="block"><span className="kicker text-[10px] block mb-2">Fund</span>
            <select value={f.fund_id} onChange={(e) => setF({...f, fund_id: e.target.value})} data-testid="req-fund" className="fld">
              {funds.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select></label>
          <label className="block"><span className="kicker text-[10px] block mb-2">Amount (USD)</span>
            <input type="number" min="0" value={f.amount} onChange={(e) => setF({...f, amount: e.target.value})} data-testid="req-amount" className="fld font-mono" /></label>
          {f.kind === "redemption" && (
            <label className="block"><span className="kicker text-[10px] block mb-2">Or Units</span>
              <input type="number" min="0" step="0.0001" value={f.units} onChange={(e) => setF({...f, units: e.target.value})} data-testid="req-units" className="fld font-mono" /></label>
          )}
          <button data-testid="req-submit" className="btn-gold px-6 py-3 text-sm uppercase tracking-widest w-full">Submit Request</button>
          <style>{`.fld{width:100%;background:hsl(226 20% 12%);border:1px solid hsl(226 20% 14%);padding:0.6rem 0.8rem;font-size:.875rem;color:inherit}`}</style>
        </form>
      </div>
      <div>
        <h2 className="text-3xl mb-6">Request History</h2>
        <div className="space-y-2">
          {reqs.map((r) => (
            <div key={r.id} className="surface p-4 flex justify-between items-center" data-testid={`req-row-${r.id}`}>
              <div>
                <div className="text-sm">{r.kind.toUpperCase()} · {r.fund_id}</div>
                <div className="text-xs text-muted-foreground font-mono">{fmtDateTime(r.created_at)}</div>
              </div>
              <div className="text-right">
                <div className="font-mono">{r.amount ? fmtUSD(r.amount) : `${fmtNum(r.units, 4)} u`}</div>
                <div className={`text-xs uppercase tracking-widest ${r.status === "approved" ? "pos" : r.status === "rejected" ? "neg" : "gold"}`}>{r.status}</div>
              </div>
            </div>
          ))}
          {reqs.length === 0 && <div className="text-sm text-muted-foreground">No requests yet.</div>}
        </div>
      </div>
    </div>
  );
}

function MyReferrals() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/client/referrals").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const badge = (row) => {
    if (row.status === "paid") return <span className="text-xs px-2 py-0.5 border border-primary text-primary uppercase tracking-widest">Paid</span>;
    if (row.ready_to_pay) return <span className="text-xs px-2 py-0.5 border border-green-500 text-green-500 uppercase tracking-widest">Ready to pay</span>;
    if (row.year_1_locked) return <span className="text-xs px-2 py-0.5 border border-muted-foreground text-muted-foreground uppercase tracking-widest">Year-1 closed</span>;
    return <span className="text-xs px-2 py-0.5 border border-border text-muted-foreground uppercase tracking-widest">Accruing</span>;
  };
  return (
    <div>
      <div className="mb-8">
        <div className="kicker mb-2">Reward Program</div>
        <h1 className="text-4xl mb-3">My Referrals</h1>
        <p className="text-sm text-muted-foreground max-w-3xl leading-relaxed">
          Piplight pays you <span className="gold">15% of the profit</span> generated by every investor you introduce, during their first 12 months from fund inception. Rewards are paid <span className="text-foreground">once</span>, at the end of that year. There is no cap on the number of referrals.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <Tile label="Accruing (unpaid)" value={<span className="gold">{fmtUSD(data.total_accrued_unpaid)}</span>} testid="ref-accrued" />
        <Tile label="Lifetime Paid" value={fmtUSD(data.total_paid_lifetime)} testid="ref-paid" />
        <Tile label="Active Referees" value={data.rows.filter((r) => r.status !== "paid").length} testid="ref-active" />
      </div>

      {data.rows.length === 0 ? (
        <div className="surface p-8 text-center">
          <div className="kicker mb-3 text-primary">No referrals yet</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            When you introduce a new investor and they subscribe to a Piplight fund, we'll link them to you here and start tracking your 15% commission automatically.
          </p>
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="ref-table">
          <thead className="text-muted-foreground text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left pb-3">Referee</th>
              <th className="text-right pb-3">Their Profit (Y1)</th>
              <th className="text-right pb-3">Your Accrued 15%</th>
              <th className="text-left pb-3 pl-4">Payable On</th>
              <th className="text-right pb-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.id} className="border-t border-border row-hover" data-testid={`ref-row-${r.id}`}>
                <td className="py-3">
                  <div>{r.referee_name || "Referred investor"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{r.referee_email_masked}</div>
                </td>
                <td className={`py-3 text-right font-mono ${r.referee_profit_ytd >= 0 ? "pos" : "neg"}`}>
                  {fmtUSD(r.referee_profit_ytd)}
                </td>
                <td className="py-3 text-right font-mono gold">
                  {fmtUSD(r.status === "paid" ? r.amount_paid : r.accrued_commission_15pct)}
                </td>
                <td className="py-3 pl-4 font-mono text-xs">
                  {r.status === "paid" && r.paid_at ? (
                    <>
                      <span className="text-primary">Paid {fmtDate(r.paid_at)}</span>
                      {r.payment_reference && (
                        <div className="text-[10px] text-muted-foreground">Ref: {r.payment_reference}</div>
                      )}
                    </>
                  ) : r.year_end_date ? (
                    <>
                      {fmtDate(r.year_end_date)}
                      {!r.year_1_locked && r.days_remaining_in_y1 !== null && (
                        <div className="text-[10px] text-muted-foreground">{r.days_remaining_in_y1} days remaining</div>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-3 text-right">{badge(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-10 surface p-5">
        <div className="kicker mb-2 text-primary">How it works</div>
        <ul className="text-sm text-muted-foreground leading-relaxed space-y-2 list-disc pl-5">
          <li><strong className="text-foreground">Rate:</strong> 15% of the referee's realised + unrealised profit during their first 12 months.</li>
          <li><strong className="text-foreground">Window:</strong> Starts on the referee's fund-inception date and ends exactly 365 days later.</li>
          <li><strong className="text-foreground">Payout:</strong> Paid once, shortly after the year-1 anniversary. Method (wire / internal credit / cheque) is agreed with our operations team.</li>
          <li><strong className="text-foreground">No cap:</strong> Rewards from multiple referees stack indefinitely.</li>
          <li>Individual trade information about your referee is never exposed — only their aggregate profit contributes to your accrual.</li>
        </ul>
      </div>
    </div>
  );
}

function Statements() {
  const [funds, setFunds] = useState([]);
  const [active, setActive] = useState(null);
  const [stmt, setStmt] = useState(null);
  useEffect(() => { api.get("/funds").then((r) => { setFunds(r.data); setActive(r.data[0]?.id); }); }, []);
  useEffect(() => {
    if (!active) return;
    api.get(`/client/statement/${active}`).then((r) => setStmt(r.data)).catch(() => setStmt(null));
  }, [active]);
  return (
    <div>
      <h1 className="text-3xl mb-6">Statements & Documents</h1>
      <div className="flex gap-2 mb-6">
        {funds.map((f) => (
          <button key={f.id} onClick={() => setActive(f.id)} data-testid={`stmt-tab-${f.id}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest border ${active === f.id ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>{f.code}</button>
        ))}
      </div>
      {stmt ? (
        <div className="surface p-8 space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="kicker mb-1">Statement</div>
              <h2 className="text-2xl">{stmt.fund.name}</h2>
            </div>
            <div className="text-right">
              <div className="kicker mb-1">Generated</div>
              <div className="font-mono text-sm">{fmtDateTime(stmt.generated_at)}</div>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <Tile label="Units" value={fmtNum(stmt.holding.unit_balance, 4)} />
            <Tile label="NAV/Unit" value={fmtNum(stmt.nav_per_unit, 4)} />
            <Tile label="Value" value={fmtUSD(stmt.holding_value)} />
            <Tile label="HWM" value={fmtNum(stmt.holding.high_water_mark_nav, 4)} />
          </div>
          <div>
            <div className="kicker mb-3">Transactions</div>
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-xs uppercase tracking-widest">
                <tr><th className="text-left pb-2">Date</th><th className="text-left pb-2">Type</th><th className="text-right pb-2">Units</th><th className="text-right pb-2">NAV</th><th className="text-right pb-2">Amount</th></tr>
              </thead>
              <tbody>
                {stmt.transactions.map((t) => (
                  <tr key={t.id} className="border-t border-border">
                    <td className="py-2 font-mono">{fmtDate(t.timestamp)}</td>
                    <td className="py-2 uppercase text-xs">{t.type}</td>
                    <td className="py-2 text-right font-mono">{fmtNum(t.units_delta, 4)}</td>
                    <td className="py-2 text-right font-mono">{fmtNum(t.nav_per_unit_applied, 4)}</td>
                    <td className="py-2 text-right font-mono">{fmtUSD(t.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={() => window.print()} data-testid="stmt-print" className="btn-gold px-6 py-2 text-xs uppercase tracking-widest">Print / Save PDF</button>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">No holding in this fund.</div>
      )}
    </div>
  );
}

export default function ClientPortal() {
  return (
    <Routes>
      <Route index element={<Dashboard />} />
      <Route path="holdings" element={<Holdings />} />
      <Route path="transactions" element={<Transactions />} />
      <Route path="requests" element={<Requests />} />
      <Route path="referrals" element={<MyReferrals />} />
      <Route path="statements" element={<Statements />} />
      <Route path="documents" element={<Documents />} />
      <Route path="deposit" element={<Deposit />} />
    </Routes>
  );
}
